// Base URL: kosong (relative) saat dev (di-proxy Vite ke wrangler dev, lihat
// vite.config.ts) atau saat Worker+Pages di-serve dari domain yang sama.
// Production cross-domain: set VITE_API_BASE saat build (lihat deploy.yml).
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** true kalau server barusan menolak password (dipakai App.tsx untuk redirect ke login). */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const password = localStorage.getItem('muidsoft:authPassword');

  // Jangan set Content-Type untuk FormData — biarkan browser set multipart boundary otomatis.
  // Untuk body JSON (string), set Content-Type: application/json secara eksplisit.
  const isFormData = init?.body instanceof FormData;

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(!isFormData && init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(password ? { 'X-Auth-Password': password } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    onUnauthorized?.();
  }

  if (!res.ok) {
    let message = `Request gagal (${res.status})`;
    try {
      // API selalu return { error: { message: string, details?: unknown } }
      const body = (await res.json()) as { error?: { message?: string } | string };
      if (typeof body.error === 'object' && body.error?.message) {
        message = body.error.message;
      } else if (typeof body.error === 'string') {
        message = body.error;
      }
    } catch {
      // response bukan JSON, pakai pesan default
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * Upload multipart/form-data dengan auth header.
   * JANGAN pakai api.post() untuk FormData — itu akan set Content-Type: application/json
   * dan JSON.stringify FormData-nya (hasilnya salah). Method ini tidak set Content-Type
   * sama sekali supaya browser otomatis set multipart boundary yang benar.
   */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form, headers: {} }),
};

/**
 * Streaming asset URL (video/thumbnail/voice) — dipakai langsung di
 * <video>/<img> src, bukan lewat request(). Tag <video>/<img> tidak bisa
 * kirim custom header, jadi password auth dilampirkan sebagai query param
 * `?auth=` (didukung middleware/auth.ts di backend).
 */
export function assetUrl(path: string): string {
  const password = localStorage.getItem('muidsoft:authPassword');
  if (!password) return `${API_BASE}${path}`;
  const separator = path.includes('?') ? '&' : '?';
  return `${API_BASE}${path}${separator}auth=${encodeURIComponent(password)}`;
}
